<script setup lang="ts">
import { inject, onMounted, onUnmounted, computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useFormsStore } from '@/stores/forms'
import { useTeamsStore } from '@/stores/teams'
import { useOpsStore } from '@/stores/ops'
import { useAuthStore } from '@/stores/auth'
import { useSyncStore } from '@/stores/sync'
import { useToastStore } from '@/stores/toast'
import { useIAPStore } from '@/stores/iap'
import { useObjectivesStore } from '@/stores/objectives'
import { useHazardsStore } from '@/stores/hazards'
import { useOrgAssignmentStore } from '@/stores/orgAssignment'
import { useMedicalPlanStore } from '@/stores/medicalPlan'
import { useCommsStore } from '@/stores/comms'
import { FormType, TeamType } from '@/types'
import type { Timestamp } from 'firebase/firestore'
import FosButton from '@/components/ui/FosButton.vue'

const formsStore = useFormsStore()
const teamsStore = useTeamsStore()
const opsStore = useOpsStore()
const authStore = useAuthStore()
const syncStore = useSyncStore()
const toast = useToastStore()
const iapStore = useIAPStore()
const objectivesStore = useObjectivesStore()
const hazardsStore = useHazardsStore()
const orgAssignmentStore = useOrgAssignmentStore()
const medicalPlanStore = useMedicalPlanStore()
const commsStore = useCommsStore()
const router = useRouter()
const incidentId = inject<string>('incidentId', '')

const FORM_CARDS = [
  { formType: FormType.ICS201,       name: 'Incident Briefing',      identifier: 'ICS 201' },
  { formType: FormType.ICS211Header, name: 'Agency Check-In List',   identifier: 'ICS 211' },
  { formType: FormType.ICS214,       name: 'Activity Log',           identifier: 'ICS 214' },
  { formType: FormType.SAR104S,      name: 'Team Assignments',        identifier: 'SAR 104S' },
  { formType: FormType.SAR110,       name: 'Team Debrief',           identifier: 'SAR 110' },
  { formType: FormType.SAR111,       name: 'K9 Team Debrief',        identifier: 'SAR 111' },
  { formType: FormType.SAR134,       name: 'Clue / Evidence Report', identifier: 'SAR 134/135' },
]

function subscribeToOp(opId: string) {
  iapStore.subscribeToIAP(incidentId, opId)
  objectivesStore.subscribeToObjectives(incidentId, opId)
  hazardsStore.subscribeToHazards(incidentId, opId)
  orgAssignmentStore.subscribeToOrgAssignment(incidentId, opId)
  medicalPlanStore.subscribeToMedicalPlan(incidentId, opId)
  commsStore.subscribe(incidentId, opId)
}

onMounted(() => {
  if (incidentId) {
    formsStore.subscribeToForms(incidentId)
    const opId = opsStore.selectedOpId
    if (opId) subscribeToOp(opId)
  }
})

watch(() => opsStore.selectedOpId, (opId) => {
  if (opId) subscribeToOp(opId)
})

onUnmounted(() => {
  objectivesStore.teardown()
  hazardsStore.teardown()
  orgAssignmentStore.teardown()
  medicalPlanStore.teardown()
  commsStore.teardown()
})

const hasK9Teams = computed(() =>
  teamsStore.teams.some((t) => t.team_type === TeamType.K9),
)

const iapStatus = computed(() => iapStore.iap?.status ?? 'not_started')
const currentOpNumber = computed(() => opsStore.selectedOp?.op_number ?? '—')

const iapStatusBadge = computed(() => {
  if (iapStatus.value === 'draft') return { label: 'Draft', cls: 'text-amber-700 bg-amber-50 border-amber-300' }
  return { label: 'Not started', cls: 'text-outline bg-surface-container-low border-border' }
})

type StepId = 'cover' | '202' | '203' | '205' | '206' | '207' | '208'
type StepStatus = 'complete' | 'partial' | 'empty'

const stepStatuses = computed<Record<StepId, StepStatus>>(() => {
  const objCount = objectivesStore.objectives.length
  const hazCount = hazardsStore.hazards.length
  const chCount = commsStore.channels.length
  const hasIC = !!(orgAssignmentStore.orgAssignment?.incident_commander?.name)
  const medPlan = medicalPlanStore.medicalPlan
  const hasmedical = (medPlan?.aid_stations?.length ?? 0) > 0 || (medPlan?.hospitals?.length ?? 0) > 0
  return {
    cover: 'complete',
    '202': objCount > 0 ? 'complete' : 'empty',
    '203': hasIC ? 'complete' : 'empty',
    '205': chCount > 0 ? 'complete' : 'empty',
    '206': hasmedical ? 'complete' : 'empty',
    '207': 'complete',
    '208': hazCount > 0 ? 'complete' : 'empty',
  }
})

const completedCount = computed(() =>
  (Object.values(stepStatuses.value) as StepStatus[]).filter(s => s === 'complete').length,
)

const IAP_SECTION_PILLS: { code: string; label: string; id: StepId }[] = [
  { code: 'C',   label: 'Cover',   id: 'cover' },
  { code: '202', label: 'ICS 202', id: '202' },
  { code: '203', label: 'ICS 203', id: '203' },
  { code: '205', label: 'ICS 205', id: '205' },
  { code: '206', label: 'ICS 206', id: '206' },
  { code: '207', label: 'ICS 207', id: '207' },
  { code: '208', label: 'ICS 208', id: '208' },
]

const iapLastEdited = computed(() => {
  const ts = iapStore.iap?.created_at
  if (!ts) return null
  const d = (ts as { toDate?: () => Date }).toDate?.()
  if (!d) return null
  return d.toLocaleString()
})

function lastEditedText(formType: string): string {
  const form = formsStore.forms[formType]
  if (!form?.last_updated_at) return 'Not started'
  const ts = form.last_updated_at as Timestamp
  const date = ts.toDate?.()
  return date ? date.toLocaleString() : 'Not started'
}

const FORM_ROUTES: Record<string, string> = {
  [FormType.ICS201]:       'incident-form-ics201',
  [FormType.ICS211Header]: 'incident-form-ics211',
  [FormType.ICS214]:       'incident-form-ics214',
  [FormType.SAR104S]:      'incident-form-sar104s-list',
  [FormType.SAR110]:       'incident-form-sar110-list',
  [FormType.SAR111]:       'incident-form-sar111-list',
  [FormType.SAR134]:       'incident-form-sar134',
}

function navigate(formType: string) {
  const name = FORM_ROUTES[formType] ?? 'incident-form-editor'
  router.push({ name, params: { id: incidentId, formType } })
}

const exportingForms = ref<Record<string, boolean>>({})

async function exportPdf(formType: string, event: Event) {
  event.stopPropagation()
  if (!syncStore.isOnline) {
    toast.add('PDF export requires a network connection.', 'warning')
    return
  }
  exportingForms.value[formType] = true
  try {
    const token = await authStore.currentUser?.getIdToken()
    const res = await fetch(`/api/incidents/${incidentId}/forms/${formType}/export-pdf`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
    const body = await res.json()
    if (!res.ok || body.error) {
      throw new Error(body.error?.message ?? 'Export failed')
    }
    window.open(body.data.pdf_url, '_blank')
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Export failed'
    toast.add(message, 'error')
  } finally {
    exportingForms.value[formType] = false
  }
}
</script>

<template>
  <div class="h-full flex flex-col overflow-hidden">
    <div class="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
      <div>
        <h2 class="font-headline font-black text-lg uppercase tracking-tight text-on-surface">Forms</h2>
        <p class="font-mono text-xs text-outline mt-0.5">{{ FORM_CARDS.filter((c) => c.formType !== FormType.SAR111 || hasK9Teams).length }} form types</p>
      </div>
    </div>

    <!-- IAP Hero Card -->
    <div class="px-6 pt-4 pb-2">
      <div class="bg-surface border border-border rounded grid" style="grid-template-columns: 1fr auto;">
        <!-- Left -->
        <div class="flex flex-col gap-3.5 p-[18px_22px] border-r border-border/60">
          <!-- Header -->
          <div class="flex items-center gap-3">
            <span class="w-9 h-9 shrink-0 bg-primary text-white inline-flex items-center justify-content:center font-mono font-bold text-[11px] tracking-[0.10em] uppercase rounded items-center justify-center">IAP</span>
            <h2 class="font-headline font-black text-[24px] uppercase tracking-[0.02em] m-0 leading-none">Incident Action Plan</h2>
          </div>
          <!-- Subtext -->
          <p class="font-mono text-[12px] tracking-[0.04em] text-outline m-0">OP {{ currentOpNumber }} · Incident Action Plan</p>
          <!-- Section pills -->
          <div class="flex gap-1.5 flex-wrap">
            <button
              v-for="pill in IAP_SECTION_PILLS"
              :key="pill.code"
              type="button"
              :class="[
                'inline-flex items-center gap-1 px-2 py-1 border rounded font-mono text-[11px] tracking-[0.06em] cursor-pointer transition-opacity hover:opacity-80',
                stepStatuses[pill.id] === 'complete'
                  ? 'bg-green-50 border-green-300 text-green-700'
                  : stepStatuses[pill.id] === 'partial'
                    ? 'bg-amber-50 border-amber-300 text-amber-700'
                    : 'bg-surface-container-low border-border text-outline opacity-60',
              ]"
              @click="router.push({ name: 'incident-forms-iap', params: { id: incidentId }, query: { step: pill.id } })"
            >
              <span class="w-1.5 h-1.5 rounded-[1px] bg-current opacity-75" />
              {{ pill.label }}
            </button>
          </div>
          <!-- Last edited -->
          <p v-if="iapLastEdited" class="font-mono text-[11px] text-outline m-0">Last edited {{ iapLastEdited }}</p>
        </div>

        <!-- Right -->
        <div class="flex flex-col justify-between gap-3.5 p-[18px_22px] min-w-[280px]">
          <!-- Status badge -->
          <span
            :class="[
              'inline-flex self-start font-mono text-[11px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 border rounded',
              iapStatusBadge.cls,
            ]"
          >{{ iapStatusBadge.label }}</span>

          <!-- Completeness progress -->
          <div class="flex flex-col gap-1.5">
            <span class="font-mono text-[10px] tracking-[0.12em] uppercase text-outline">Completeness</span>
            <div class="flex items-baseline gap-1.5">
              <strong class="font-headline font-black text-[30px] leading-none">{{ completedCount }}</strong>
              <span class="font-mono font-medium text-[14px] text-outline">/ 7 ready</span>
            </div>
            <div class="h-1.5 bg-surface-container-low border border-border overflow-hidden">
              <i
                class="block h-full bg-primary transition-all"
                :style="{ width: `${(completedCount / 7) * 100}%` }"
              />
            </div>
          </div>

          <!-- CTA buttons -->
          <div class="flex gap-2">
            <FosButton
              variant="secondary"
              size="sm"
              class="flex-1 justify-center"
              :disabled="!syncStore.isOnline"
              @click.stop="() => {}"
            >
              <span class="material-symbols-outlined text-[14px] mr-1">picture_as_pdf</span>
              Export
            </FosButton>
            <FosButton
              variant="primary"
              size="sm"
              style="flex: 2;"
              class="justify-center"
              @click="router.push({ name: 'incident-forms-iap', params: { id: incidentId } })"
            >
              Open IAP Builder
              <span class="material-symbols-outlined text-[14px] ml-1">arrow_forward</span>
            </FosButton>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 px-6 pb-6 pt-2">
      <div
        v-for="card in FORM_CARDS.filter((c) => c.formType !== FormType.SAR111 || hasK9Teams)"
        :key="card.formType"
        class="relative group bg-surface-container hover:bg-surface-container-high cursor-pointer p-4 border rounded border-border"
        @click="navigate(card.formType)"
      >
        <div class="flex items-start justify-between gap-2 mb-2">
          <span class="text-sm font-semibold text-on-surface">{{ card.name }}</span>
          <div class="flex items-center gap-1">
            <span class="text-[10px] font-label text-outline uppercase tracking-widest whitespace-nowrap">{{ card.identifier }}</span>
            <button
              class="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-outline hover:text-on-surface"
              title="Export as PDF"
              @click="exportPdf(card.formType, $event)"
            >
              <span v-if="exportingForms[card.formType]" class="w-4 h-4 border-2 border-outline/30 border-t-outline animate-spin inline-block" />
              <span v-else class="material-symbols-outlined text-[16px]">download</span>
            </button>
          </div>
        </div>
        <p class="text-xs text-outline">
          <template v-if="formsStore.forms[card.formType]?.last_updated_at">
            Last edited: {{ lastEditedText(card.formType) }}
          </template>
          <template v-else>Not started</template>
        </p>
      </div>
    </div>
  </div>
</template>
